<script setup lang="ts">
import axios from 'axios'
import { Loader2, Variable } from 'lucide-vue-next'

import EmojiPickerInput from '@/views/review/Components/EmojiPickerInput.vue'
import ReviewVariablesList from '@/views/review/Components/ReviewVariablesList.vue'
import useReview from '@/composables/review'
import type { reviewReplyTemplateInterface } from '@/types/review'

interface MoodOption {
  id: number
  title: string
}

const props = defineProps<{
  template?: reviewReplyTemplateInterface
}>()

const emit = defineEmits<{
  (e: 'created'): void
  (e: 'canceled'): void
  (e: 'updated', template: reviewReplyTemplateInterface): void
}>()

const { t } = useI18n()
const notification = useNotification()
const { resolveMoodLabel } = useReview()

const displayVariablesList = ref(false)
const isProcessing = ref(false)

const model = ref({
  reviewReplyTemplateMoodId: props.template?.reviewReplyTemplateMood?.id ?? 1,
  title: props.template?.title ?? '',
  content: props.template?.content ?? '',
  emoji: props.template?.emoji ?? '🙂'
})

const errors = ref<{ title: string[]; content: string[] }>({ title: [], content: [] })

const moodOptions = computed<MoodOption[]>(() =>
  [1, 2, 3].map((mood) => ({ id: mood, title: t(resolveMoodLabel(mood) ?? '') }))
)

const selectedMood = computed({
  get: () => moodOptions.value.find((mood) => mood.id === model.value.reviewReplyTemplateMoodId),
  set: (option?: MoodOption) => {
    if (option) model.value.reviewReplyTemplateMoodId = option.id
  }
})

const resetErrors = () => {
  errors.value = { title: [], content: [] }
}

const validateForm = () => {
  resetErrors()

  if (!model.value.title) {
    errors.value.title.push(t('pages.review.reviewPage.notifications.error.title'))
  }
  if (!model.value.content) {
    errors.value.content.push(t('pages.review.reviewPage.notifications.error.content'))
  }

  return !errors.value.title.length && !errors.value.content.length
}

const setVariableUsed = (text: string) => {
  model.value.content += ` ${text}`
}

const submit = async () => {
  if (!validateForm()) return

  isProcessing.value = true
  const payload = {
    title: model.value.title,
    content: model.value.content,
    emoji: model.value.emoji,
    reviewReplyTemplateMoodId: model.value.reviewReplyTemplateMoodId
  }

  try {
    if (props.template?.id) {
      const { data } = await axios.put(`/reviews-reply-templates/${props.template.id}`, {
        ...payload,
        isFavorite: props.template.isFavorite
      })
      notification.create('success', {
        text: t('pages.review.reviewPage.notifications.success.updateTemplate')
      })
      emit('updated', data.data)
      return
    }

    await axios.post('/reviews-reply-templates', { ...payload, isFavorite: 0 })
    notification.create('success', {
      text: t('pages.review.reviewPage.notifications.success.addTemplate')
    })
    emit('created')
  } catch {
    notification.create('error', {
      text: props.template?.id
        ? t('pages.review.reviewPage.notifications.error.updateTemplate')
        : t('pages.review.reviewPage.notifications.error.addTemplate'),
      persist: true
    })
    isProcessing.value = false
  }
}
</script>

<template>
  <Card class="gap-4 bg-muted py-4">
    <CardHeader class="px-4">
      <CardTitle class="text-base">
        {{
          template
            ? t('pages.review.reviewSettingsPage.templates.form.editAdd', { template: template.title })
            : t('pages.review.reviewSettingsPage.templates.form.titleAdd')
        }}
      </CardTitle>
    </CardHeader>

    <CardContent class="flex flex-col gap-4 px-4 xl:flex-row xl:items-start">
      <EmojiPickerInput v-model="model.emoji" />

      <div class="flex flex-1 flex-col gap-4">
        <div class="flex flex-col gap-2">
          <Label>{{ t('pages.review.reviewSettingsPage.templates.form.mood.title') }}</Label>
          <AppRadioCard v-model="selectedMood" :options="moodOptions" class="grid gap-3 sm:grid-cols-3" />
        </div>

        <AppFormInput
          id="review-template-title"
          v-model="model.title"
          full-width
          :label="t('pages.review.reviewSettingsPage.templates.form.title.label')"
          :placeholder="t('pages.review.reviewSettingsPage.templates.form.title.placeholder')"
          :errors="errors.title"
          @input="resetErrors"
          @keyup.enter="submit"
        />

        <div class="flex flex-col gap-2">
          <Label for="review-template-content">
            {{ t('pages.review.reviewSettingsPage.templates.form.content.label') }}
          </Label>
          <Textarea
            id="review-template-content"
            v-model="model.content"
            rows="3"
            class="bg-background"
            :aria-invalid="errors.content.length > 0"
            :placeholder="t('pages.review.reviewSettingsPage.templates.form.content.placeholder')"
            @input="resetErrors"
          />
          <p v-if="errors.content.length" class="text-sm text-destructive">
            {{ errors.content[0] }}
          </p>
        </div>

        <div class="flex justify-end">
          <AppTooltip tooltip-position="top">
            <Button
              variant="outline"
              size="icon"
              :class="displayVariablesList ? 'text-muted-foreground' : 'text-primary'"
              @click="displayVariablesList = !displayVariablesList"
            >
              <Variable />
            </Button>
            <template #tooltip-content>
              {{
                displayVariablesList
                  ? t('pages.review.reviewPage.items.hideAvailableVariables')
                  : t('pages.review.reviewPage.items.availableVariables')
              }}
            </template>
          </AppTooltip>
        </div>

        <ReviewVariablesList v-if="displayVariablesList" @variable-used="setVariableUsed" />
      </div>
    </CardContent>

    <CardFooter class="justify-end gap-2 px-4">
      <Button variant="outline" @click="emit('canceled')">
        {{ t('pages.review.reviewSettingsPage.templates.buttons.cancel') }}
      </Button>
      <Button variant="default-primary" :disabled="isProcessing" @click="submit">
        <Loader2 v-if="isProcessing" class="animate-spin" />
        {{ t('pages.review.reviewSettingsPage.templates.buttons.save') }}
      </Button>
    </CardFooter>
  </Card>
</template>
